import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, Button, Stack, Tooltip,
  FormControl, Select, MenuItem, InputLabel, ToggleButton, ToggleButtonGroup,
  TextField, InputAdornment, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  Autocomplete, createFilterOptions,
  FormControlLabel, Switch, Popover, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MapIcon from '@mui/icons-material/Map';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/Error';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GridViewIcon from '@mui/icons-material/GridView';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryIcon from '@mui/icons-material/History';
import EmpresaSelect from '../../../../components/EmpresaSelect';
import api from '../../../../services/api';
import { TopologyApiProvider, useTopologyBase } from './topologyApi';
import useRebuildCliente, { formataEspera } from './useRebuildCliente';
import CyTopologyMap from './CyTopologyMap';
import { protocolsToKinds } from './LayerToggles';
import EdgeTrafficDialog from './EdgeTrafficDialog';
import HostDrawer from './HostDrawer';
import IssuesPanel from './IssuesPanel';
import ModeToolbar from './ModeToolbar';
import TablesPanel from './TablesPanel';
import ChangesPanel from './ChangesPanel';
import useTopologyPoll from './useTopologyPoll';
import useTopologyTraffic from './useTopologyTraffic';
import useTopologyMetrics from './useTopologyMetrics';
import { SUPERFICIE } from '../snapshot/estilo';

// DEFAULT_PROTOCOLS é só pra manter compat com CyTopologyMap `activeKinds`.
// ViewMode agora gerencia visibilidade de edges — esse set fica "tudo on".
const DEFAULT_PROTOCOLS = new Set(['bgp', 'ospf', 'p2p', 'vlan_lan', 'mpls', 'l2', 'static']);

const SETTINGS_KEY = (empresaId) => `topology:settings:${empresaId}`;

// Autocomplete do MUI monta no DOM toda opção que passa no filtro. Cortar em
// 40 é o que mantém a lista utilizável num parque de centenas de equipamentos.
const LIMITE_HOSTS = 40;
const filtroHost = createFilterOptions({
  limit: LIMITE_HOSTS,
  stringify: (o) => `${o.label} ${o.mgmt_ip || ''} ${o.vendor || ''} ${o.tipo || ''}`,
});

// Espaçamento dos layouts. Era um slider Compacto/Explodir, removido: os
// layouts hierárquico e quadriculado já garantem distância mínima e
// não-sobreposição por conta própria, então afrouxar ou apertar por fora só
// desfazia essa garantia. 40 é o valor em que as duas foram medidas.
const ESPACAMENTO = 40;

function loadSettings(empresaId) {
  if (!empresaId) return null;
  try {
    const raw = localStorage.getItem(SETTINGS_KEY(empresaId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveSettings(empresaId, patch) {
  if (!empresaId) return;
  try {
    const current = loadSettings(empresaId) || {};
    localStorage.setItem(SETTINGS_KEY(empresaId), JSON.stringify({ ...current, ...patch }));
  } catch {
    // ignore quota / privacy mode
  }
}

/**
 * Casca que escolhe a empresa e injeta o caminho da API.
 *
 * Existe separada da view porque um Provider so alcanca os hooks dos
 * DESCENDENTES: `useTopologyPoll` e companhia sao chamados dentro da view, entao
 * o provider precisa estar acima dela. Com tudo num componente so, os hooks
 * leriam o contexto de fora e receberiam null para sempre.
 */
export default function TopologyPage() {
  const [empresaId, setEmpresaId] = useState(null);
  return (
    <TopologyApiProvider base={empresaId ? `/admin/topology/${empresaId}` : null}>
      <TopologyView empresaId={empresaId} setEmpresaId={setEmpresaId} />
    </TopologyApiProvider>
  );
}

export function TopologyView({ empresaId, setEmpresaId, modo = 'admin' }) {
  const ehCliente = modo === 'cliente';
  // Mesmo caminho base que os hooks e paineis usam. Faltava aqui: a view lia
  // `${base}` em /maps sem nunca ter declarado a variavel.
  const base = useTopologyBase();
  const rebuildCli = useRebuildCliente(ehCliente);
  const [mapId, setMapId] = useState(null);
  const [availableMaps, setAvailableMaps] = useState([]);

  const [activeProtocols, setActiveProtocols] = useState(DEFAULT_PROTOCOLS);
  const [showIssues, setShowIssues] = useState(true);
  const [activeHostFilters, setActiveHostFilters] = useState(new Set());
  // Hierárquico é o default. 'explodido' (fcose) foi removido: layout de força
  // trata todo nó igual, produz nuvem e espalha componente desconexo pelo
  // canvas, então o fit encolhia tudo pra 17%.
  const [viewMode, setViewMode] = useState('radial');
  const [pageView, setPageView] = useState('mapa'); // mapa | tabelas | mudancas
  const [selectedNode, setSelectedNode] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedVlans, setSelectedVlans] = useState(() => new Set());
  const [highlightedRing, setHighlightedRing] = useState(null);
  const [pathTrace, setPathTrace] = useState({ source: null, target: null });
  const [pathResult, setPathResult] = useState(null);
  const [pathMode, setPathMode] = useState('reliable');
  const [osiModes, setOsiModes] = useState(() => new Set(['all']));
  const [l3Protocols, setL3Protocols] = useState(() => new Set(['bgp']));
  const [l3StrictHosts, setL3StrictHosts] = useState(false);
  const [selectedIface, setSelectedIface] = useState(null);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(true);
  const [hostFoco, setHostFoco] = useState(null);
  const [edgeDialog, setEdgeDialog] = useState({ open: false, edge: null });
  const [unfoldParallel, setUnfoldParallel] = useState(false);
  const [showTraffic, setShowTraffic] = useState(true);
  const [showMetrics, setShowMetrics] = useState(true);
  const [fillSysmapEdges, setFillSysmapEdges] = useState(false);
  const [implodeLeaves, setImplodeLeaves] = useState(false);
  const [hostTypeFilter, setHostTypeFilter] = useState(() => new Set(['switch_router', 'radio', 'olt']));
  const DEFAULT_LAYOUT_TUNING = {
    repulsion_base: 15000, repulsion_step: 1000,
    edge_base: 300, edge_step: 5,
    sep_base: 280, sep_step: 4,
    star_ring: 400, city_spacing: 1300, quad_offset: 1800,
  };
  const [layoutTuning, setLayoutTuning] = useState(DEFAULT_LAYOUT_TUNING);

  // Um controle só pro que o usuário enxerga como "o que estou vendo".
  // Por dentro continuam dois estados: pageView diz se é mapa/tabela/mudanças,
  // e viewMode qual layout de mapa. Trocar pra Tabelas preserva o layout
  // escolhido, então voltar pro mapa cai onde estava.
  const noMapa = pageView === 'mapa';
  const modoAtivo = noMapa ? viewMode : pageView;
  const handleModo = (v) => {
    if (!v) return;
    if (v === 'tabelas' || v === 'mudancas') { setPageView(v); return; }
    setPageView('mapa');
    setViewMode(v);
  };

  const { graph, alarms, loading, error, reload } = useTopologyPoll({ mapId });
  // Só dispara traffic polling quando o grafo está efetivamente carregado (não em needs_rebuild)
  const { traffic } = useTopologyTraffic({
    enabled: noMapa && !!graph && graph.needs_rebuild !== true,
  });
  const { metrics: nodeMetrics, sessions: edgeSessions } = useTopologyMetrics({
    enabled: noMapa && !!graph && graph.needs_rebuild !== true && showMetrics,
  });

  const cyRef = useRef(null);

  const activeKinds = useMemo(() => protocolsToKinds(activeProtocols), [activeProtocols]);

  // Cardinalidade de cada chip de protocolo no grafo carregado. Os chips eram
  // fixos no código e não diziam quanto afetavam: BGP tem 2 arestas de 225 e
  // MPLS pode ter 0 — clicar parecia não fazer nada. Com a contagem, chip
  // vazio some e o resto mostra o tamanho antes do clique.
  const protocolCounts = useMemo(() => {
    const m = new Map([['bgp', 0], ['ospf_adj', 0], ['static_route', 0], ['mpls', 0]]);
    if (!graph?.edges) return m;
    const nodeById = new Map((graph.nodes || []).map((n) => [String(n.id), n]));
    for (const e of graph.edges) {
      for (const k of e.kinds || []) {
        if (m.has(k)) m.set(k, m.get(k) + 1);
      }
      // MPLS não é kind de aresta: é overlay, quando as duas pontas falam MPLS
      // ou algum endpoint tem VRF. Conta pela mesma regra que o mapa aplica.
      const a = nodeById.get(String(e.source));
      const b = nodeById.get(String(e.target));
      const ep = e.endpoints || {};
      if (ep.a?.vrf || ep.b?.vrf || (a?.has_mpls && b?.has_mpls)) {
        m.set('mpls', m.get('mpls') + 1);
      }
    }
    return m;
  }, [graph]);

  // Flat list de interfaces físicas pro L1 port picker.
  // Extrai de cada node as interfaces que tem `is_physical` OU `lag_parent` OU
  // qualquer vlan_allowed/pvid/dot1q (i.e. que realmente carregam tráfego L2/L1).
  const flatInterfaces = useMemo(() => {
    if (!graph?.nodes) return [];
    const out = [];
    for (const n of graph.nodes) {
      if (n.is_virtual) continue;
      for (const i of (n.interfaces || [])) {
        if (!i.name) continue;
        const vlans = [
          ...(i.vlan_allowed || []),
          ...(i.pvid ? [i.pvid] : []),
          ...(i.dot1q_tag ? [i.dot1q_tag] : []),
        ];
        out.push({
          host_id: n.id,
          host_label: n.label || n.id,
          name: i.name,
          admin_down: i.admin_down,
          lag_parent: i.lag_parent,
          vlans: Array.from(new Set(vlans)).sort((a, b) => a - b),
          port_type: i.port_type,
          ips: i.ips || [],
        });
      }
    }
    return out;
  }, [graph]);

  // Aggregated rings pra o L2 picker
  const aggregatedRings = useMemo(() => {
    if (!graph?.nodes) return [];
    const map = new Map();
    for (const n of graph.nodes) {
      for (const r of (n.erps_rings || [])) {
        const existing = map.get(r.ring_id) || {
          ring_id: r.ring_id,
          members: new Set(),
          control_vlan: r.control_vlan,
          rpl_owner: null,
        };
        existing.members.add(String(n.id));
        if (r.role === 'rpl_owner') existing.rpl_owner = n.label || String(n.id);
        map.set(r.ring_id, existing);
      }
    }
    return Array.from(map.values()).map((r) => ({
      ring_id: r.ring_id,
      members: r.members.size,
      control_vlan: r.control_vlan,
      rpl_owner: r.rpl_owner,
    }));
  }, [graph]);

  const handleSelectIface = useCallback((ifaceOrNull) => {
    setSelectedIface(ifaceOrNull);
    if (!ifaceOrNull) {
      cyRef.current?.highlightVlanSet(new Set());
      cyRef.current?.limparFoco();
      return;
    }
    // Antes isto só acendia as VLANs da porta — e porta sem VLAN não acendia
    // nada, então buscar uma interface parecia não fazer efeito. Faltava o
    // óbvio: mostrar O EQUIPAMENTO dono da porta. Uma interface não existe
    // solta no mapa; quem se procura é a máquina onde ela está.
    cyRef.current?.focarNo(ifaceOrNull.host_id);
    if (ifaceOrNull.vlans?.length > 0) {
      cyRef.current?.highlightVlanSet(new Set(ifaceOrNull.vlans));
    }
  }, []);

  // Busca lista de sysmaps SÓ depois que o grafo carregou (evita corrida)
  useEffect(() => {
    if (!empresaId) {
      setAvailableMaps([]);
      setMapId(null);
      return;
    }
    if (!graph) return;
    if (ehCliente) return;
    api.get(`${base}/maps`)
      .then((res) => setAvailableMaps(res.data?.maps || []))
      .catch(() => setAvailableMaps([]));
  }, [empresaId, graph, ehCliente, base]);

  const toggleProtocol = useCallback((key) => {
    setActiveProtocols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleHostFilter = useCallback((key) => {
    setActiveHostFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleVlanSelection = useCallback((vidSet) => {
    // limita a 10 VLANs simultâneas pra não poluir
    const capped = vidSet.size > 10
      ? new Set(Array.from(vidSet).slice(0, 10))
      : vidSet;
    setSelectedVlans(capped);
    cyRef.current?.highlightVlanSet(capped);
    if (capped.size > 0) {
      setHighlightedRing(null);
      cyRef.current?.highlightRing(null);
    }
  }, []);

  const handleRingHighlight = useCallback((ringId) => {
    setHighlightedRing(ringId);
    cyRef.current?.highlightRing(ringId);
    if (ringId != null) {
      setSelectedVlans(new Set());
      cyRef.current?.highlightVlanSet(new Set());
    }
  }, []);

  const handlePathTraceChange = useCallback(({ source, target }) => {
    setPathTrace({ source, target });
    const result = cyRef.current?.tracePath(source, target, selectedVlans, pathMode);
    setPathResult(result || null);
  }, [selectedVlans, pathMode]);

  // Aplica OsiMode no Cytoscape quando muda + reaplica quando grafo atualiza
  useEffect(() => {
    if (!cyRef.current) return;
    cyRef.current.setViewMode(osiModes, l3Protocols, { strictHosts: l3StrictHosts });
    // eslint-disable-next-line
  }, [osiModes, l3Protocols, l3StrictHosts, graph?.generated_at]);

  const handleNodeClick = useCallback((nodeId) => {
    if (!graph) return;
    const node = (graph.nodes || []).find((n) => String(n.id) === String(nodeId));
    if (node) {
      setSelectedNode(node);
      setDrawerOpen(true);
    }
  }, [graph]);

  const handleIssueClick = useCallback((issue) => {
    if (!graph || !issue?.hosts?.length) return;
    const node = (graph.nodes || []).find((n) => String(n.id) === String(issue.hosts[0]));
    if (node) {
      setSelectedNode(node);
      setDrawerOpen(true);
      cyRef.current?.centerOnNode(node.id);
    }
  }, [graph]);

  // Restaura settings salvos ao trocar de empresa (filtros OSI, modo, etc).
  // Usa ref pra evitar que o effect dispare o save abaixo em cascata.
  const skipNextSaveRef = useRef(false);
  useEffect(() => {
    if (!empresaId) return;
    const saved = loadSettings(empresaId);
    if (!saved) return;
    skipNextSaveRef.current = true;
    if (Array.isArray(saved.osiModes)) setOsiModes(new Set(saved.osiModes));
    if (Array.isArray(saved.l3Protocols)) setL3Protocols(new Set(saved.l3Protocols));
    if (typeof saved.l3StrictHosts === 'boolean') setL3StrictHosts(saved.l3StrictHosts);
    if (typeof saved.unfoldParallel === 'boolean') setUnfoldParallel(saved.unfoldParallel);
    if (typeof saved.showTraffic === 'boolean') setShowTraffic(saved.showTraffic);
    if (typeof saved.fillSysmapEdges === 'boolean') setFillSysmapEdges(saved.fillSysmapEdges);
    if (typeof saved.implodeLeaves === 'boolean') setImplodeLeaves(saved.implodeLeaves);
    if (Array.isArray(saved.hostTypeFilter)) setHostTypeFilter(new Set(saved.hostTypeFilter));
    // zabbix só é restaurado se ainda houver sysmap; senão cai no default.
    // 'explodido' foi removido — quem tinha esse valor salvo cai no default.
    if (saved.viewMode === 'radial' || saved.viewMode === 'grid') setViewMode(saved.viewMode);
    if (saved.layoutTuning && typeof saved.layoutTuning === 'object') {
      setLayoutTuning({ ...DEFAULT_LAYOUT_TUNING, ...saved.layoutTuning });
    }
  }, [empresaId]);

  // Persiste filtros quando mudam (após o load inicial)
  useEffect(() => {
    if (!empresaId) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    saveSettings(empresaId, {
      osiModes: Array.from(osiModes),
      l3Protocols: Array.from(l3Protocols),
      l3StrictHosts,
      unfoldParallel,
      showTraffic,
      fillSysmapEdges,
      implodeLeaves,
      hostTypeFilter: Array.from(hostTypeFilter),
      layoutTuning,
      viewMode,
    });
  }, [empresaId, osiModes, l3Protocols, l3StrictHosts, unfoldParallel, showTraffic, fillSysmapEdges, implodeLeaves, hostTypeFilter, layoutTuning, viewMode]);

  const handleFit = () => cyRef.current?.fit();

  // Busca de equipamento. Lista completa dos nós, filtrada enquanto digita e
  // cortada em LIMITE_HOSTS — o Autocomplete do MUI monta no DOM toda opção
  // que passa no filtro, e com 479 nós ele tentava desenhar as 479 de uma vez.
  const hostOptions = useMemo(() => {
    if (!graph?.nodes) return [];
    // Nó virtual entra na busca. Ele NÃO é equipamento, mas está desenhado no
    // mapa — o segmento de broadcast (a sub-rede compartilhada) e o AS externo
    // são justamente o que alguém procura quando quer entender "quem está
    // nessa /29" ou "onde entra o AS 15169". Deixá-los de fora fazia a busca
    // não achar metade do que a tela mostra.
    return graph.nodes
      .map((n) => {
        const seg = n.role === 'segment' || n.is_broadcast_domain;
        const ext = n.is_virtual && !seg;
        return {
          id: n.id,
          label: n.label || String(n.id),
          tipo: seg ? 'segmento' : ext ? 'AS externo' : 'equipamento',
          seg,
          ext,
          mgmt_ip: n.mgmt_ip,
          vendor: n.vendor,
          member_count: n.member_count,
          issues: n.issues_count || 0,
        };
      })
      // equipamento primeiro: é o caso comum, e misturar 51 virtuais no meio
      // de 428 reais faria a lista parecer poluída
      .sort((a, b) => (a.seg || a.ext ? 1 : 0) - (b.seg || b.ext ? 1 : 0)
        || a.label.localeCompare(b.label));
  }, [graph]);

  // Escolher o equipamento não basta: num mapa de 479 nós, centralizar sem
  // destacar deixa ele igual aos outros 478. `focarNo` centraliza, dá zoom,
  // ofusca o resto e mantém os vizinhos legíveis.
  const handleFocarHost = (opt) => {
    setHostFoco(opt);
    if (!opt) { cyRef.current?.limparFoco(); return; }
    const achou = cyRef.current?.focarNo(opt.id);
    if (!achou) cyRef.current?.limparFoco();
  };

  const needsRebuild = graph?.needs_rebuild === true;
  const hasGraphData = graph && !needsRebuild;
  const totalNodes = hasGraphData ? (graph?.nodes?.length || 0) : 0;
  const totalEdges = hasGraphData ? (graph?.edges?.length || 0) : 0;
  // O payload traz `issues_total`; a lista completa não vem mais (443 KB pra
  // 1.797 achados que a tela agrupava por tipo antes de mostrar).
  const totalIssues = hasGraphData
    ? (graph?.issues_total ?? graph?.issues?.length ?? 0)
    : 0;
  const activeAlarms = Object.keys(alarms || {}).length;

  // Estimativa de nodes visíveis baseada nos osiModes (multi-select) — usado
  // pra mostrar contador "visível N de M" na toolbar.
  const visibleCounts = useMemo(() => {
    if (!hasGraphData || !osiModes || osiModes.has('all') || osiModes.size === 0) {
      return { nodes: totalNodes, edges: totalEdges };
    }
    const nodes = graph?.nodes || [];
    // Union de hosts visíveis em qualquer mode selecionado
    const visibleSet = new Set();
    nodes.forEach((n) => {
      if (osiModes.has('l1') && !n.is_virtual) visibleSet.add(n.id);
      if (osiModes.has('l2') && !n.is_virtual &&
          (n.has_vlans || n.l2_mode === 'access_switch' || n.l2_mode === 'l3_switch')) {
        visibleSet.add(n.id);
      }
      if (osiModes.has('l3')) {
        if (n.is_virtual && l3Protocols.has('bgp')) visibleSet.add(n.id);
        else if (l3Protocols.has('bgp') && n.has_bgp) visibleSet.add(n.id);
        else if (l3Protocols.has('ospf_adj') && n.has_ospf) visibleSet.add(n.id);
        else if (l3Protocols.has('mpls') && n.has_mpls) visibleSet.add(n.id);
        else if (l3Protocols.has('static_route')) visibleSet.add(n.id);
      }
      if (osiModes.has('health') && n.issues_count > 0) visibleSet.add(n.id);
    });
    return { nodes: visibleSet.size, edges: totalEdges };
  }, [hasGraphData, osiModes, l3Protocols, graph, totalNodes, totalEdges]);

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{
        p: 2,
        mb: 1,
        ...SUPERFICIE,
        // slate-50 em vez do branco da SUPERFICIE: a barra de controles fica um
        // tom atras dos paineis de conteudo, que e o que separa comando de dado.
        bgcolor: '#f8fafc',
        '& .MuiButton-outlined': { borderColor: '#cbd5e1', color: '#0f172a' },
      }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="h6">Topologia da rede</Typography>
          {/* O cliente nao escolhe empresa: a API resolve pelo token dele.
              Nao e so esconder controle — nao existe parametro de empresa nas
              rotas de cliente, entao nao ha o que selecionar. */}
          {!ehCliente && (
            <Box sx={{ minWidth: 260 }}>
              <EmpresaSelect
                fullWidth
                size="small"
                value={empresaId}
                handler={setEmpresaId}
                label="Empresa"
              />
            </Box>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={modoAtivo}
            onChange={(_, v) => handleModo(v)}
          >
            <ToggleButton value="radial">
              <AccountTreeIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Hierárquico
            </ToggleButton>
            <ToggleButton value="grid">
              <GridViewIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Quadriculado
            </ToggleButton>
            <ToggleButton value="zabbix" disabled={availableMaps.length === 0}>
              <MapIcon fontSize="small" sx={{ mr: 0.5 }} />
              Mapa Zabbix
            </ToggleButton>
            <ToggleButton value="tabelas">
              <TableChartIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Tabelas
            </ToggleButton>
            <ToggleButton value="mudancas">
              <HistoryIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Mudanças
            </ToggleButton>
          </ToggleButtonGroup>
          {noMapa && viewMode === 'zabbix' && availableMaps.length > 0 && (
            <Box sx={{ minWidth: 220 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sysmap</InputLabel>
                <Select
                  value={mapId || ''}
                  label="Sysmap"
                  onChange={(e) => setMapId(e.target.value || null)}
                >
                  <MenuItem value=""><em>Automático (maior)</em></MenuItem>
                  {availableMaps.map((m) => (
                    <MenuItem key={m.map_id} value={m.map_id}>
                      {m.name} ({m.host_count} hosts)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {activeAlarms > 0 && (
            <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 700 }}>
              {activeAlarms} alarmes
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {/* Os cinco interruptores ficam juntos, numa caixa com borda. Antes o
              filtro por tipo de host caía no MEIO deles, e a barra virava uma
              fileira de controles sem agrupamento nenhum — quem procurava
              "tráfego" tinha que varrer tudo. Agrupar não é enfeite: é o que
              deixa achar por posição em vez de por leitura. */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            border: '1px solid #e2e8f0', borderRadius: 1, px: 1, py: 0.2,
            bgcolor: '#ffffff',
          }}>
          <Tooltip title="Unfold paralelos: quando há múltiplas L3 ifaces/VLANs distintas entre 2 hosts (ex: BGP em Vlanif100 + Vlanif200), desdobra em linhas separadas pra cada caminho L3 ficar explícito.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  disabled={!noMapa}
                  checked={unfoldParallel}
                  onChange={(e) => setUnfoldParallel(e.target.checked)}
                />
              }
              label={<Typography variant="caption">unfold</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
          <Tooltip title="Implodir folhas: esconde access/OLT e mostra badge +N no pai (border/core/dist). Click num anchor explode só os filhos dele num grid ao lado. ESC implode tudo.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  disabled={!noMapa}
                  checked={implodeLeaves}
                  onChange={(e) => setImplodeLeaves(e.target.checked)}
                />
              }
              label={<Typography variant="caption">implodir</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
          <Tooltip title="Métricas de host: CPU%, MEM%, Temp°C, PPPoE sessions. Polling 15s via Zabbix.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  disabled={!noMapa}
                  checked={showMetrics}
                  onChange={(e) => setShowMetrics(e.target.checked)}
                />
              }
              label={<Typography variant="caption">métricas</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
          <Tooltip title="Overlay de tráfego Zabbix (bps por iface). Desligue quando o mapa estiver cheio de linhas pra reduzir poluição visual.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  disabled={!noMapa}
                  checked={showTraffic}
                  onChange={(e) => setShowTraffic(e.target.checked)}
                />
              }
              label={<Typography variant="caption">tráfego</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
          {noMapa && viewMode === 'zabbix' && (
            <Tooltip title="Completar edges faltantes do Zabbix sysmap: quando o mapa do Zabbix tem links desenhados manualmente entre 2 hosts mas nossa inferência não tem, adiciona eles como edges suplementares (cor cinza, kind=sysmap).">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={fillSysmapEdges}
                    onChange={(e) => setFillSysmapEdges(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">completar edges</Typography>}
                sx={{ m: 0 }}
              />
            </Tooltip>
          )}
          </Box>

          <Tooltip title="Filtrar por tipo de host: switch/router, rádio (wireless/RB), OLT. Desmarca os tipos que quer esconder.">
            <ToggleButtonGroup
              size="small"
              disabled={!noMapa}
              value={Array.from(hostTypeFilter)}
              onChange={(_, v) => setHostTypeFilter(new Set(v))}
              sx={{ '& .MuiToggleButton-root': { px: 0.8, py: 0.2, fontSize: 10 } }}
            >
              <ToggleButton value="switch_router">SW/RTR</ToggleButton>
              <ToggleButton value="radio">Rádio</ToggleButton>
              <ToggleButton value="olt">OLT</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          {totalIssues > 0 && (
            <Tooltip title="Ver lista de issues">
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<ErrorIcon fontSize="small" />}
                disabled={!noMapa}
                onClick={() => setIssuesOpen(true)}
                sx={{ fontWeight: 700 }}
              >
                {totalIssues}
              </Button>
            </Tooltip>
          )}
          <Autocomplete
            size="small"
            options={hostOptions}
            filterOptions={filtroHost}
            value={hostFoco}
            onChange={(_, v) => handleFocarHost(v)}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            disabled={!noMapa || !hasGraphData}
            sx={{ width: 280 }}
            noOptionsText={`digite pra buscar — ${hostOptions.length} equipamentos`}
            renderOption={(props, o) => {
              const { key, ...rest } = props;
              return (
                <Box component="li" key={o.id} {...rest}
                     sx={{ display: 'block !important', py: 0.4 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                    {o.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {o.seg
                      ? `segmento · ${o.member_count || 0} equipamentos compartilham`
                      : o.ext
                        ? 'AS externo'
                        : `${o.mgmt_ip || 's/ IP'} · ${o.vendor || '?'}`}
                    {o.issues ? ` · ${o.issues} issue${o.issues > 1 ? 's' : ''}` : ''}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="ir para nó"
                placeholder={`${hostOptions.length} nós — equipamento, segmento ou AS`} />
            )}
          />
          <Tooltip title="Centraliza no grafo">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleFit}
                disabled={!graph || !noMapa}
              >
                <CenterFocusStrongIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          {/* Admin reconstrói direto; cliente passa pela rota com trava de 15
              min, porque o rebuild custa 8,6s de CPU e 171 MB na maior empresa
              medida e o worker já refaz sozinho a cada 2h. O botão diz quanto
              falta em vez de só ficar cinza. */}
          <Tooltip title={ehCliente
            ? (rebuildCli.faltam > 0
                ? `Disponível em ${formataEspera(rebuildCli.faltam)}`
                : 'Reconstrói o mapa a partir dos backups mais recentes')
            : 'Força rebuild (2-10s)'}
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                color={ehCliente ? 'primary' : 'error'}
                startIcon={(loading || rebuildCli.rodando)
                  ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={ehCliente
                  ? async () => { if (await rebuildCli.rebuild()) reload(); }
                  : reload}
                disabled={ehCliente
                  ? (rebuildCli.rodando || rebuildCli.faltam > 0)
                  : (loading || !empresaId)}
              >
                {ehCliente && rebuildCli.faltam > 0
                  ? formataEspera(rebuildCli.faltam)
                  : 'Reconstruir'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        {rebuildCli.erro && (
          <Alert severity="warning" sx={{ mt: 1 }} onClose={rebuildCli.limparErro}>
            {rebuildCli.erro}
          </Alert>
        )}
      </Paper>

      {/* Estado vazio: a pergunta e diferente para cada audiencia.
          O admin pode nao ter escolhido empresa — o cliente NAO TEM o que
          escolher, entao "Selecione uma empresa" ali era instrucao impossivel de
          cumprir. Para ele o vazio so pode significar mapa nao construido, e o
          proprio payload diz o porque em `empty_reason`. */}
      {(!ehCliente && !empresaId) ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', ...SUPERFICIE, borderColor: '#e2e8f0' }}>
          <Typography color="text.secondary">Selecione uma empresa pra carregar a topologia.</Typography>
        </Paper>
      ) : (ehCliente && !loading && (graph?.needs_rebuild || !totalNodes)) ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', ...SUPERFICIE, borderColor: '#e2e8f0' }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            {graph?.needs_rebuild ? 'O mapa ainda não foi construído.' : 'Nenhum equipamento no mapa.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {graph?.empty_reason === 'no hosts with backup'
              ? 'A topologia é montada a partir das configurações salvas pelo backup. Nenhum equipamento desta empresa tem backup ainda.'
              : (graph?.empty_reason
                  || 'Use “Reconstruir” para montar o mapa a partir dos backups mais recentes.')}
          </Typography>
          {rebuildCli.faltam > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#64748b' }}>
              Reconstrução disponível em {formataEspera(rebuildCli.faltam)}.
            </Typography>
          )}
        </Paper>
      ) : (
        <>
          {/* Row 2 do toolbar: filtros do mapa. Em Tabelas/Mudanças some
              inteira — camada, protocolo, VLAN e anel não filtram nada ali. */}
          {noMapa && (
          <ModeToolbar
            protocolCounts={protocolCounts}
            osiModes={osiModes}
            onChangeOsiModes={setOsiModes}
            l3Protocols={l3Protocols}
            onChangeL3Protocols={setL3Protocols}
            l3StrictHosts={l3StrictHosts}
            onChangeL3StrictHosts={setL3StrictHosts}
            interfaces={flatInterfaces}
            selectedIface={selectedIface}
            onChangeSelectedIface={handleSelectIface}
            vlansIndex={graph?.vlans_index || []}
            selectedVlans={selectedVlans}
            onChangeSelectedVlans={handleVlanSelection}
            rings={aggregatedRings}
            highlightedRing={highlightedRing}
            onChangeHighlightedRing={handleRingHighlight}
          />
          )}

          <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
            {pageView === 'tabelas' && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, bgcolor: 'background.default' }}>
                <TablesPanel />
              </Box>
            )}
            {pageView === 'mudancas' && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, overflow: 'auto', bgcolor: 'background.default' }}>
                <ChangesPanel />
              </Box>
            )}
            <Paper variant="outlined" sx={{
              height: '100%', minHeight: 500, position: 'relative', overflow: 'hidden',
              ...SUPERFICIE, borderColor: '#e2e8f0',
            }}>
              {loading && (
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: hasGraphData ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.7)',
                  zIndex: 10,
                }}>
                  <CircularProgress />
                </Box>
              )}
              {hasGraphData ? (
                <CyTopologyMap
                  ref={cyRef}
                  graph={graph}
                  alarms={alarms}
                  traffic={traffic}
                  activeKinds={activeKinds}
                  activeHostFilters={activeHostFilters}
                  showIssues={showIssues}
                  viewMode={viewMode}
                  explosionLevel={ESPACAMENTO}
                  unfoldParallel={unfoldParallel}
                  showTraffic={showTraffic}
                  nodeMetrics={nodeMetrics}
                  edgeSessions={edgeSessions}
                  fillSysmapEdges={fillSysmapEdges}
                  implodeLeaves={implodeLeaves}
                  hostTypeFilter={hostTypeFilter}
                  layoutTuning={layoutTuning}
                  onNodeClick={handleNodeClick}
                  onEdgeClick={(edgeData) => setEdgeDialog({ open: true, edge: edgeData })}
                />
              ) : needsRebuild ? (
                <Box sx={{
                  p: 4, height: '100%', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 2,
                }}>
                  <Typography variant="h6" color="text.secondary">
                    Mapa ainda não construído pra essa empresa
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, textAlign: 'center' }}>
                    O worker automático rebuilda a cada 15min. Ou clique abaixo pra forçar um rebuild agora
                    (leva de 2 a 30 segundos, dependendo do tamanho da empresa).
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                    onClick={reload}
                    disabled={loading}
                  >
                    {loading ? 'Construindo...' : 'Construir mapa agora'}
                  </Button>
                </Box>
              ) : (
                !loading && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Sem dados.</Typography>
                  </Box>
                )
              )}
              {hasGraphData && (
                <Box sx={{
                  position: 'absolute', bottom: 12, right: 12, zIndex: 5,
                  bgcolor: 'rgba(15,23,42,0.94)', color: '#e2e8f0',
                  border: '1px solid #334155', borderRadius: 1,
                  p: legendOpen ? 1 : 0.3,
                  maxWidth: legendOpen ? 260 : 'auto',
                  fontFamily: 'monospace',
                }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" sx={{ flexGrow: 1, fontWeight: 700, color: '#cbd5e1' }}>
                      {legendOpen ? 'Legenda' : 'L'}
                    </Typography>
                    <IconButton size="small" onClick={() => setLegendOpen(!legendOpen)} sx={{ p: 0.2, color: '#94a3b8' }}>
                      {legendOpen ? <ClearIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
                    </IconButton>
                  </Stack>
                  {legendOpen && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mb: 0.3 }}>Linhas</Typography>
                      {[
                        { c: '#06b6d4', s: 'solid',  l: 'p2p (L1)' },
                        { c: '#14b8a6', s: 'solid',  l: 'trunk (L2)' },
                        { c: '#14b8a6', s: 'dashed', l: 'shared_lan' },
                        { c: '#64748b', s: 'dotted', l: 'l2_desc heurístico' },
                        { c: '#f97316', s: 'solid',  l: 'BGP' },
                        { c: '#0ea5e9', s: 'solid',  l: 'OSPF' },
                        { c: '#d946ef', s: 'solid',  l: 'MPLS overlay' },
                        { c: '#64748b', s: 'dotted', l: 'static route' },
                        { c: '#ef4444', s: 'dashed', l: 'session DOWN ⚠' },
                      ].map((it) => (
                        <Stack key={it.l} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.2 }}>
                          <Box sx={{ width: 28, height: 0, borderTop: `2px ${it.s} ${it.c}`, flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ fontSize: 10 }}>{it.l}</Typography>
                        </Stack>
                      ))}
                      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 1, mb: 0.3 }}>Nós</Typography>
                      {[
                        ['hexágono', 'BGP border'],
                        ['◇ losango', 'core / RR'],
                        ['cloud dashed', 'AS externo'],
                        ['◈ pequeno', 'segmento (sub-rede compartilhada)'],
                        ['borda vermelha', 'alarme/issue'],
                        ['borda dashed', 'sem backup'],
                        ['col. esquerda', 'isolado'],
                      ].map(([h, l]) => (
                        <Stack key={l} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.2 }}>
                          <Typography variant="caption" sx={{ fontSize: 9, color: '#94a3b8', minWidth: 86 }}>{h}</Typography>
                          <Typography variant="caption" sx={{ fontSize: 10 }}>{l}</Typography>
                        </Stack>
                      ))}
                      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 1, mb: 0.3 }}>Comportamento</Typography>
                      <Typography variant="caption" sx={{ fontSize: 10, display: 'block', mb: 0.2 }}>
                        Bundled (2+ kinds) → 1 linha por protocolo
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 10, display: 'block', mb: 0.2 }}>
                        L3 chip solo → destaca + atenua resto
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 10, display: 'block' }}>
                        Implodir + click anchor → explode estrela
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        </>
      )}

      <HostDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        node={selectedNode}
        graph={graph}
      />

      <EdgeTrafficDialog
        open={edgeDialog.open}
        onClose={() => setEdgeDialog({ open: false, edge: null })}
        edge={edgeDialog.edge}
        graph={graph}
      />

      <Dialog
        open={issuesOpen}
        onClose={() => setIssuesOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: SUPERFICIE }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          <Typography variant="h6" component="span">Issues ({totalIssues})</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <IssuesPanel
            resumo={graph?.issues_resumo || []}
            total={totalIssues}
            onIssueClick={(issue) => {
              handleIssueClick(issue);
              setIssuesOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
